import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { updateFidelityMaximizer } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import * as Sentry from '@sentry/react'

export const fidelityMaximizerModuleActions = {
  FIDELITY_MAXIMIZER_LOAD_STATUS: 'FIDELITY_MAXIMIZER_LOAD_STATUS',
  FIDELITY_MAXIMIZER_TRIGGER_ACTION: 'FIDELITY_MAXIMIZER_TRIGGER_ACTION',
  FIDELITY_MAXIMIZER_ISSUE_REGISTRATION_CODE: 'FIDELITY_MAXIMIZER_ISSUE_REGISTRATION_CODE',
}

export const PENDING_REGISTRATION_SCENARIO = 'myselectra_registration_pending'

const mapStatusResponse = data => {
  const scenarioKey = data?.scenario_key ?? null

  return {
    loading: false,
    loaded: true,
    enabled: data?.enabled === true,
    checklist: data?.checklist ?? {
      nlbp_classic: false,
      nlbp_personalized: false,
      myselectra: false,
      app_downloaded: false,
    },
    isPdlEligible: data?.is_pdl_eligible === true,
    missingContactFields: data?.missing_contact_fields ?? [],
    scenarioKey,
    actionKey: data?.action_key ?? null,
    actionAvailable: data?.action_available === true,
    actionKeys: Array.isArray(data?.action_keys) ? data.action_keys : null,
    pendingRegistration: data?.pending_registration ?? null,
    ...(scenarioKey === PENDING_REGISTRATION_SCENARIO ? {} : { registrationCode: null }),
    error: null,
  }
}

export function* loadFidelityMaximizerStatus({ contactId }) {
  if (!contactId) {
    return
  }

  yield put(updateFidelityMaximizer({ loading: true, error: null }))

  try {
    const { fidelityMaximizerStatusUrl } = yield select(selectExtraRoutesAuthData)
    const response = yield call(callApi, {
      endpoint: fidelityMaximizerStatusUrl,
      method: 'GET',
      asteriskReplacement: contactId,
      showErrorSnackbar: false,
    })

    yield put(updateFidelityMaximizer(mapStatusResponse(response?.data)))
  } catch (error) {
    yield put(updateFidelityMaximizer({ loading: false, loaded: true, error: error?.message || null }))

    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('loadFidelityMaximizerStatusError'), { extra: { error } })
    }
  }
}

export function* triggerFidelityMaximizerAction({ contactId, action, enqueue }) {
  yield put(updateFidelityMaximizer({ actionLoading: true, actionResult: null }))

  try {
    const { fidelityMaximizerActionUrl } = yield select(selectExtraRoutesAuthData)

    // Success/failure arrives later via useFidelityActionRealtimeSync, not this call's response.
    yield call(callApi, {
      endpoint: fidelityMaximizerActionUrl,
      method: 'POST',
      asteriskReplacement: contactId,
      payload: { action },
      showErrorSnackbar: false,
    })
  } catch (error) {
    yield put(updateFidelityMaximizer({ actionLoading: false }))

    const backendMessage = error?.responseData?.error?.message || error?.message || "Erreur lors de l'envoi."
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: backendMessage,
    })

    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('triggerFidelityMaximizerActionError'), { extra: { error } })
    }
  }
}

export function* issueFidelityMaximizerRegistrationCode({ contactId, enqueue }) {
  yield put(updateFidelityMaximizer({ codeLoading: true }))

  try {
    const { fidelityMaximizerRegistrationCodeUrl } = yield select(selectExtraRoutesAuthData)
    const response = yield call(callApi, {
      endpoint: fidelityMaximizerRegistrationCodeUrl,
      method: 'POST',
      asteriskReplacement: contactId,
      showErrorSnackbar: false,
    })

    yield put(
      updateFidelityMaximizer({
        codeLoading: false,
        actionResult: null,
        registrationCode: {
          contactId,
          code: response?.data?.code ?? '',
          expiresAt: response?.data?.expires_at ?? null,
          issuedAt: new Date().toISOString(),
        },
      }),
    )
  } catch (error) {
    yield put(updateFidelityMaximizer({ codeLoading: false, registrationCode: null }))

    const backendMessage = error?.responseData?.error?.message || error?.message || "Erreur lors de l'envoi."
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: backendMessage,
    })

    // A rejected code can mean the pending row is gone on the backend; the status says which scenario applies now.
    yield call(loadFidelityMaximizerStatus, { contactId })

    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('issueFidelityMaximizerRegistrationCodeError'), { extra: { error } })
    }
  }
}
